import { useState } from 'react';
import classNames from 'classnames';
import { Card, Col, Form, Row } from 'react-bootstrap';
import LineTimeChart from '@/app/common/components/LineTimeChart';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import numberHumanizer from '@/helpers/numberHumanizer';
import { getColor } from '@/helpers/utils';
import { useAppContext } from '@/Main';

export interface MetricLineTimeChartMetric {
  id: string;
  label: string;
  total: number | string;
  data: number[];
  color: string;
  axisLabel?: string;
  inverse?: boolean;
  axisFormatter?: (value: number | string) => string;
  tooltip?: string;
}

export interface MetricLineTimeChartDateRange {
  start_date: string;
  end_date: string;
}

export interface MetricLineTimeChartProps {
  dates?: number[];
  metrics?: MetricLineTimeChartMetric[];
  defaultActiveIds?: string[];
  isFullDate?: boolean;
  topLabel?: string;
  className?: string;
  onTimeRangeChange?: (range: MetricLineTimeChartDateRange) => void;
}

const IMPRESSIONS_LIGHT_COLOR = '#a855f7';

const getMetricColor = (metric: MetricLineTimeChartMetric, isDark: boolean) => {
  if (metric.id === 'impressions' && !isDark) {
    return IMPRESSIONS_LIGHT_COLOR;
  }

  return metric.color;
};

const MetricLineTimeChart = ({
  dates = [],
  metrics = [],
  defaultActiveIds = [],
  isFullDate = true,
  topLabel = '',
  className = '',
  onTimeRangeChange
}: MetricLineTimeChartProps) => {
  const {
    config: { isDark }
  } = useAppContext() as unknown as { config: { isDark: boolean } };

  const [activeIds, setActiveIds] = useState<string[]>(
    defaultActiveIds.length
      ? defaultActiveIds
      : metrics.slice(0, 2).map(metric => metric.id)
  );

  const toggleMetric = (id: string) => {
    setActiveIds(prev => {
      if (prev.includes(id) && prev.length === 1) {
        return prev;
      }

      return prev.includes(id)
        ? prev.filter(item => item !== id)
        : [...prev, id];
    });
  };

  const activeMetrics = metrics.filter(metric => activeIds.includes(metric.id));

  const chartSeries = activeMetrics.map((metric, index) => ({
    name: metric.label,
    data: metric.data,
    color: getMetricColor(metric, isDark),
    axisLabel: metric.axisLabel,
    inverse: metric.inverse,
    axisFormatter: metric.axisFormatter,
    yAxisIndex: index
  }));

  const formatTotal = (metric: MetricLineTimeChartMetric) => {
    if (metric.id === 'ctr') {
      return `${String(metric.total).toPersianDigit()}%`;
    }

    if (metric.id === 'position') {
      return String(metric.total).toPersianDigit();
    }

    return numberHumanizer(metric.total)?.toString()?.toPersianDigit();
  };

  return (
    <div className={className}>
      <div className="mb-3">
        <Row className="g-2">
          {metrics.map(metric => {
            const isActive = activeIds.includes(metric.id);
            const checkboxId = `metric-line-chart-${metric.id}`;
            const metricColor = getMetricColor(metric, isDark);

            return (
              <Col key={metric.id} xs={12} sm={6} xl={3}>
                <Card
                  className={classNames('h-100 cursor-pointer', {
                    'border-0 text-white': isActive,
                    'border border-300': !isActive
                  })}
                  style={
                    isActive
                      ? { backgroundColor: metricColor }
                      : undefined
                  }
                  onClick={() => toggleMetric(metric.id)}
                >
                  <Card.Body className="p-3">
                    <div className="d-flex align-items-start justify-content-between gap-2">
                      <Form.Check
                        type="checkbox"
                        id={checkboxId}
                        checked={isActive}
                        onChange={() => toggleMetric(metric.id)}
                        onClick={event => event.stopPropagation()}
                        className="mb-0 remove-checked-icon rounded-circle shadow-none"
                        style={{
                          borderColor: isActive ? getColor('white') : metricColor
                        }}
                      />
                      {metric.tooltip && (
                        <HelpTooltip
                          html={metric.tooltip}
                          color={isActive ? getColor('white') : getColor('black')}
                        />
                      )}
                    </div>
                    <Form.Check.Label
                      htmlFor={checkboxId}
                      className={classNames('fs--1 mb-1 d-block cursor-pointer', {
                        'text-white': isActive,
                        'text-700': !isActive
                      })}
                      onClick={event => event.stopPropagation()}
                    >
                      {metric.label}
                    </Form.Check.Label>
                    <h4
                      className={classNames('mb-0 fw-bold', {
                        'text-white': isActive
                      })}
                    >
                      {formatTotal(metric)}
                    </h4>
                    <style>{`#${checkboxId}:checked{background-color:${metricColor};}`}</style>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>
      </div>
      <Card>
        <Card.Body>
          <LineTimeChart
            isFullDate={isFullDate}
            showName
            topLabel={topLabel}
            independentYAxis
            dates={dates}
            series={chartSeries}
            onTimeRangeChange={onTimeRangeChange}
          />
        </Card.Body>
      </Card>
    </div>
  );
};

export default MetricLineTimeChart;
